import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { type FC, useId } from "react";
import { FONT_SIZE, INK, NUMERIC_FIELD_WIDTH } from "../../../theme/tokens";
import { SystemRoundedCheckbox } from "../Clickable/SystemRoundedCheckbox";
import { SystemValidationTextField } from "./SystemValidationTextField";

/** Pass all three to add a checkbox that turns the delay on and off, or none of them for just the
 * input. Partial props are rejected: they would hide the checkbox while still disabling the input,
 * leaving no way to re-enable it. */
type CheckboxProps =
	| {
			checked: boolean;
			onCheckedChange: (checked: boolean) => void;
			/** Checkbox label. Already translated. */
			checkboxLabel: string;
	  }
	| { checked?: never; onCheckedChange?: never; checkboxLabel?: never };

type Props = CheckboxProps & {
	/** Hours to wait, as typed. Empty while the user is clearing the field. */
	value: string;
	onChange: (value: string) => void;
	/** Text after the input, e.g. "hours before shift start". Already translated. */
	suffixLabel: string;
	disabled?: boolean;
	/** Smallest delay allowed. Matches the backend. */
	minHours?: number;
	/** Shown under the input when the typed value isn't a whole number of hours. Already translated. */
	invalidHelperText?: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/** A whole number of hours at or above the minimum. Empty counts as invalid — `Number("")` is 0. */
const isWholeHoursAtLeast = (value: string, minHours: number): boolean => {
	if (value.trim() === "") return false;
	const parsed = Number(value);
	return Number.isInteger(parsed) && parsed >= minHours;
};

/**
 * "[x] Post with delay  [24] hours before shift start".
 *
 * Used everywhere a shift can be sent to an agency later instead of now. Without the checkbox props
 * only the input shows — for a delay that already exists, where the only choice left is how long.
 */
export const DelayHoursField: FC<Props> = ({
	value,
	onChange,
	suffixLabel,
	checked,
	onCheckedChange,
	checkboxLabel,
	disabled,
	minHours = 1,
	invalidHelperText,
	dataTestId,
	sx,
}) => {
	const inputId = useId();
	const hasCheckbox = checked !== undefined && onCheckedChange !== undefined;
	const inputDisabled = disabled || (hasCheckbox && !checked);

	return (
		<Box data-testid={dataTestId} sx={[{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }, ...(Array.isArray(sx) ? sx : [sx])]}>
			{hasCheckbox && (
				<SystemRoundedCheckbox
					checked={checked}
					onChange={onCheckedChange}
					label={checkboxLabel}
					disabled={disabled}
					data-testid={dataTestId ? `${dataTestId}-checkbox` : undefined}
				/>
			)}
			<SystemValidationTextField
				inputType="number"
				value={value}
				onChange={(event) => onChange(event.target.value)}
				disabled={inputDisabled}
				validation={
					invalidHelperText
						? {
								// `min` on a number input doesn't block submit, so the value is checked here too.
								validationCallback: (current) => isWholeHoursAtLeast(current, minHours),
								helperText: { invalid: invalidHelperText },
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
							}
						: undefined
				}
				// Without a message to show there is no helper text, so don't reserve a line for one.
				reserveHeightForHelperText={false}
				formControlSx={{ width: NUMERIC_FIELD_WIDTH.INLINE }}
				injectedProps={{ fullWidth: true, id: inputId }}
				inputProps={{ inputProps: { min: minHours } }}
				dataTestId={dataTestId ? `${dataTestId}-input` : undefined}
			/>
			<Typography
				component="label"
				htmlFor={inputId}
				sx={{ fontSize: FONT_SIZE.SECONDARY, color: inputDisabled ? INK.MUTED : INK.SECONDARY, whiteSpace: "nowrap" }}
			>
				{suffixLabel}
			</Typography>
		</Box>
	);
};

export default DelayHoursField;
